import { X, Copy, Check } from "lucide-react"
import { useState } from "react"
import { cx } from "../lib/cx"
import type { Role } from "../types"

type InviteUserModalProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const roles: Role[] = ["Admin", "Staff", "Contractor"]

const InviteUserModal = ({ isOpen, onClose, onSuccess }: InviteUserModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "Staff" as Role,
  })
  const [createMagicLink, setCreateMagicLink] = useState(false)
  const [magicLink, setMagicLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCopy = () => {
    if (magicLink) {
      navigator.clipboard.writeText(magicLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleClose = () => {
    setFormData({ name: "", email: "", password: "", role: "Staff" })
    setCreateMagicLink(false)
    setMagicLink(null)
    setError(null)
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const body: Record<string, unknown> = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        createMagicLink,
      }
      if (!createMagicLink) {
        body.password = formData.password
      }

      const res = await fetch("/api/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      
      const data = await res.json()
      
      if (!res.ok) {
        throw new Error(data.error || "Failed to invite user")
      }

      if (data.magicLink) {
        setMagicLink(data.magicLink)
      } else {
        onSuccess()
        handleClose()
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to invite user")
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-[24px] bg-white p-6 shadow-xl animate-in fade-in zoom-in-95">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900">Invite Team Member</h2>
          <button onClick={handleClose} className="rounded-full p-2 hover:bg-slate-100 transition">
            <X className="h-5 w-5 text-stone" />
          </button>
        </div>

        {magicLink ? (
          <div className="space-y-6">
             <div className="bg-emerald-50 text-emerald-800 p-4 rounded-xl text-center">
                <p className="font-medium">User created successfully!</p>
                <p className="text-sm mt-1">Share this magic link with them to log in.</p>
             </div>
             
             <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Magic Link</label>
                <div className="flex gap-2">
                  <input 
                    readOnly 
                    value={magicLink} 
                    className="flex-1 rounded-xl border border-line px-4 py-2.5 text-sm bg-slate-50 text-slate-600 focus:outline-none"
                  />
                  <button 
                    onClick={handleCopy}
                    className="p-2.5 rounded-xl border border-line hover:bg-slate-50 text-stone transition"
                    title="Copy to clipboard"
                  >
                    {copied ? <Check className="h-5 w-5 text-emerald-600" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
             </div>

             <button
              onClick={() => {
                onSuccess()
                handleClose()
              }}
              className="w-full rounded-full bg-moss px-4 py-2.5 text-sm font-semibold text-white shadow-soft hover:bg-moss/90 transition"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
                placeholder="e.g. Sarah Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
                placeholder="sarah@agency.com"
              />
            </div>

            {!createMagicLink && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Set Password</label>
                <input
                  type="password"
                  required={!createMagicLink}
                  minLength={8}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
                  placeholder="Create a password"
                />
                <p className="mt-1 text-xs text-stone">Min. 8 characters</p>
              </div>
            )}

            <div className="flex items-center gap-2 py-1">
               <input 
                 type="checkbox" 
                 id="createMagicLink" 
                 checked={createMagicLink} 
                 onChange={(e) => setCreateMagicLink(e.target.checked)}
                 className="rounded border-gray-300 text-moss focus:ring-moss"
               />
               <label htmlFor="createMagicLink" className="text-sm text-slate-700 select-none cursor-pointer">
                 Generate magic link (skip password)
               </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <div className="grid grid-cols-3 gap-2">
                {roles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setFormData({ ...formData, role })}
                    className={cx(
                      "rounded-xl border px-2 py-2 text-sm font-medium transition",
                      formData.role === role
                        ? "border-moss bg-moss text-white"
                        : "border-line bg-white text-stone hover:bg-slate-50"
                    )}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-rose-600 bg-rose-50 p-3 rounded-lg">{error}</p>}

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 rounded-full border border-line px-4 py-2.5 text-sm font-semibold text-stone hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 rounded-full bg-moss px-4 py-2.5 text-sm font-semibold text-white shadow-soft hover:bg-moss/90 transition disabled:opacity-50"
              >
                {loading ? "Creating..." : "Create User"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}


export default InviteUserModal
